import type { HelpSectionContent } from './types';

// CB:BEGIN homeowner-help-content
export const homeownerHelpContent: HelpSectionContent[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Start quickly and understand how CoreBids guides every next step.',
    image: '/help/homeowner/01_landing.png',
    imageAlt: 'Homeowner landing and sign-in view ',
    tags: ['Step-by-step', 'What happens next', 'Notifications'],
    overview: 'CoreBids turns a confusing contractor search into a clear guided flow. You submit one project and follow next actions from your dashboard.',
    steps: [
      { title: 'Create or log into your account', body: 'Use the homeowner entry flow and confirm your contact details.' },
      { title: 'Open your dashboard', body: 'Your dashboard shows your status and exactly what to do next.' },
      { title: 'Use the Help button anytime', body: 'Help is built in to answer questions without leaving your workflow.' },
    ],
    nextStep: 'Create your project so contractors can start preparing bids.',
    notifications: [
      { title: 'Welcome confirmation', body: 'Confirms your account and dashboard access.', type: 'info' },
      { title: 'Action required', body: 'Prompts you if setup details are still missing.', type: 'action' },
    ],
    mistakesToAvoid: ['Skipping profile basics can delay project setup.', 'Ignoring action-required notices slows everything down.'],
    faqs: [
      { question: 'How long does setup take?', answer: 'Most homeowners finish in under 5 minutes.' },
      { question: 'Can I stop and return later?', answer: 'Yes. CoreBids saves your progress.' },
    ],
  },
  {
    id: 'creating-a-project',
    title: 'Creating a Project',
    description: 'Open a new project and define the basic scope.',
    image: '/help/homeowner/04_estimate_form.png',
    imageAlt: 'Project wizard entry and project type selection',
    tags: ['Step-by-step', 'Common issues'],
    overview: 'This is where your request is created. The clearer your setup, the better your bids.',
    steps: [
      { title: 'Start project from dashboard', body: 'Click the primary project action in the homeowner dashboard.' },
      { title: 'Choose project type', body: 'Select the closest match to your work needed.' },
      { title: 'Confirm address details', body: 'Use the exact property address where work is needed.' },
    ],
    nextStep: 'Continue to project details and upload any supporting photos.',
    notifications: [{ title: 'Project created', body: 'Confirms your project is now active in the workflow.', type: 'success' }],
    mistakesToAvoid: ['Using incomplete address details.', 'Choosing the wrong project type.'],
    faqs: [
      { question: 'Can I edit this later?', answer: 'Yes, you can update details from your project flow.' },
      { question: 'Do I need every detail now?', answer: 'No, but better detail usually improves bid quality.' },
    ],
  },
  {
    id: 'submitting-project-details',
    title: 'Submitting Project Details',
    description: 'Complete intake, upload evidence, and submit.',
    image: '/help/homeowner/04_estimate_form.png',
    imageAlt: 'Project detail form with highlighted required fields',
    tags: ['Step-by-step', 'What to click'],
    overview: 'This section gives contractors the context they need to provide useful bids quickly.',
    steps: [
      { title: 'Answer required questions', body: 'Complete timeline, scope, and property details.' },
      { title: 'Upload photos/documents', body: 'Clear images reduce clarifying questions and delays.' },
      { title: 'Review and submit', body: 'Double-check key info before submitting.' },
    ],
    nextStep: 'After submit, CoreBids transitions your project to bid collection.',
    notifications: [
      { title: 'Submission confirmation', body: 'Confirms your project details were received.', type: 'success' },
      { title: 'Missing details', body: 'Requests additional information if needed.', type: 'action' },
    ],
    mistakesToAvoid: ['Uploading blurry photos.', 'Leaving required fields blank.'],
    faqs: [
      { question: 'What photos matter most?', answer: 'Overall exterior, problem areas, and any visible damage.' },
      { question: 'Will this be shared?', answer: 'Only with relevant workflow participants for your project.' },
    ],
  },
  {
    id: 'after-you-submit',
    title: 'After You Submit',
    description: 'Track status while bids are collected.',
    image: '/help/homeowner/04_dashboard_bids_pane.png',
    imageAlt: 'Homeowner dashboard with status and next-step panel',
    tags: ['What happens next', 'Notifications'],
    overview: 'CoreBids coordinates contractor outreach and keeps your dashboard status updated.',
    steps: [
      { title: 'Monitor your status panel', body: 'It updates as your project progresses toward bids.' },
      { title: 'Respond to action-required prompts', body: 'Quick responses reduce overall project latency.' },
    ],
    nextStep: 'You will be notified as soon as bids are available.',
    notifications: [
      { title: 'Status update', body: 'Signals progression through key project states.', type: 'info' },
      { title: 'New bid available', body: 'Notifies you when contractor proposals arrive.', type: 'success' },
    ],
    mistakesToAvoid: ['Waiting too long to respond to clarifying questions.'],
    faqs: [{ question: 'How long until bids arrive?', answer: 'Often within 24 hours depending on project complexity and market conditions.' }],
  },
  {
    id: 'viewing-bids',
    title: 'Viewing Bids',
    description: 'Compare contractors clearly in one place.',
    image: '/help/homeowner/05_bids_list.png',
    imageAlt: 'Bid comparison area with highlighted details and actions',
    tags: ['Step-by-step', 'Common issues'],
    overview: 'Compare bids by scope quality, schedule confidence, and pricing—not just price alone.',
    steps: [
      { title: 'Open bid details', body: 'Review scope notes, assumptions, and timeline.' },
      { title: 'Compare at least top options', body: 'Look for clarity and fit, not only the lowest number.' },
      { title: 'Shortlist your preferred contractor', body: 'Pick the strongest overall value for your goals.' },
    ],
    nextStep: 'Accept the bid that best fits your project needs.',
    notifications: [{ title: 'Bid expiring soon', body: 'Warns you when a bid needs timely action.', type: 'warning' }],
    mistakesToAvoid: ['Selecting only by lowest price.', 'Ignoring scope details and assumptions.'],
    faqs: [{ question: 'Can I ask for changes?', answer: 'Use support or follow platform guidance for clarification steps.' }],
  },
  // CB:BEGIN bid-comparison-pdf-help-section
  {
    id: 'bid-comparison-pdf',
    title: 'Bid Comparison PDF',
    description: 'Download a one-page summary to compare bids, share with others, and keep records.',
    image: '/help/homeowner/05_bids_list.png',
    imageAlt: 'Bid comparison area with download PDF button highlighted',
    tags: ['Documents', 'Step-by-step', 'What happens next'],
    overview: 'The Bid Comparison PDF is a one-page document that captures all contractor bids for your project in a clean, readable format. Use it to review your options carefully, share them with a spouse or co-owner, or save them as a permanent record.',
    steps: [
      { title: 'Open your bids', body: 'Go to the Bids section of your project from your homeowner dashboard.' },
      { title: 'Click "Download Comparison PDF"', body: 'The button appears above your bid list as soon as at least one bid is available. A one-page PDF is generated instantly.' },
      { title: 'Review the summary', body: 'The PDF shows each contractor\'s name, price, timeline, warranty, and trust highlights side by side.' },
      { title: 'Share or save', body: 'Download and share the PDF with anyone helping you decide — a spouse, property manager, or insurance contact.' },
      { title: 'Regenerate after selection', body: 'Once you accept a contractor, click the button again. The updated PDF will clearly mark your selected contractor while preserving the full comparison for your records.' },
    ],
    nextStep: 'After reviewing the comparison, accept the bid that best fits your project needs.',
    notifications: [
      { title: 'PDF ready', body: 'The PDF is generated on demand — no wait time. It opens in a new browser tab.', type: 'info' },
    ],
    mistakesToAvoid: [
      'The Bid Comparison PDF is a comparison and record document — it is not your final project contract.',
      'Do not use the pre-selection version as a final record. Regenerate it after choosing a contractor to capture the winning bid clearly.',
    ],
    faqs: [
      {
        question: 'What is the Bid Comparison PDF?',
        answer: 'It is a downloadable one-page summary that shows all bids available for your project — including contractor names, prices, timelines, warranties, and trust highlights — in a clean, readable format.',
      },
      {
        question: 'What does the PDF include?',
        answer: 'Each bid shows the contractor name, total price, earliest start date, estimated completion, labor and material warranty years, how long they have been in business, their platform rating, and any special highlights such as Best Value, Fastest Start, Verified, or Most Trusted.',
      },
      {
        question: 'When should I use it?',
        answer: 'Use it when reviewing your options side by side, when sharing the bids with a spouse, partner, or property manager before deciding, or when you want to keep a permanent record of all bids received for your project.',
      },
      {
        question: 'What changes after I choose a contractor?',
        answer: 'After you accept a bid, you can click "Download Comparison PDF" again. The updated version will clearly show your selected contractor while still including the other bids for comparison history.',
      },
      {
        question: 'Is this my final contract?',
        answer: 'No. The Bid Comparison PDF is a comparison and record document only. Your binding project contract is a separate agreement generated after bid acceptance.',
      },
      {
        question: 'Can I share this PDF with someone outside CoreBids?',
        answer: 'Yes. The PDF is yours to download and share. It does not contain sensitive personal information beyond your project location and the bids submitted.',
      },
    ],
  },
  // CB:END bid-comparison-pdf-help-section
  {
    id: 'selecting-a-contractor',
    title: 'Selecting a Contractor',
    description: 'Accept your chosen bid and receive your project invoice.',
    image: '/help/homeowner/06_bid_proposal_detail.png',
    imageAlt: 'Accept bid call-to-action with confirmation prompt',
    tags: ['What to click', 'What happens next'],
    overview: 'When you accept a bid, CoreBids immediately locks in the agreed price, generates your official Project Invoice & Payment Schedule, and emails it to you. The invoice is also saved in your Documents section.',
    steps: [
      { title: 'Click Accept Bid', body: 'Use the accept action on your selected proposal.' },
      { title: 'Confirm your selection', body: 'Review details and confirm in the modal.' },
      { title: 'Receive your Project Invoice', body: 'CoreBids automatically generates your Project Invoice & Payment Schedule and emails it to you. It includes the accepted bid amount, taxes and fees, the milestone payment schedule, and financing options through Enhancify.' },
      { title: 'View your invoice in Documents', body: 'Your invoice is saved in the Documents section of your project dashboard. You can download or view it any time.' },
    ],
    nextStep: 'Fund your escrow to unlock project start. Financing is available through Enhancify.',
    notifications: [
      { title: 'Bid accepted', body: 'Confirms your selection and starts next-stage tasks.', type: 'success' },
      { title: 'Invoice ready', body: 'Your Project Invoice & Payment Schedule has been generated and is available in your Documents section.', type: 'info' },
    ],
    mistakesToAvoid: ['Accepting before reviewing timeline commitments.', 'Missing the invoice email — check your spam folder if you do not see it.'],
    faqs: [
      { question: 'Can I change after accepting?', answer: 'Contact support immediately for urgent correction.' },
      { question: 'Where is my invoice?', answer: 'Go to the Documents section of your project dashboard. Your Project Invoice & Payment Schedule is listed there and can be downloaded any time.' },
      { question: 'What does the invoice include?', answer: 'The invoice shows the accepted contractor bid, taxes and fees, your total project cost, and a milestone-by-milestone payment schedule. It also includes financing options through Enhancify if you prefer a monthly payment plan.' },
      { question: 'What is Enhancify?', answer: 'Enhancify is a third-party financing partner available through CoreBids. If you prefer to pay monthly rather than fund the full escrow upfront, you can apply for financing through Enhancify directly from your invoice.' },
      { question: 'What are the milestones on my invoice?', answer: 'Your invoice shows three payment milestones: Milestone 1 (Project Start — materials ordered and work is scheduled), Milestone 2 (Demolition Complete — old roof completely removed), and Milestone 3 (Project Complete — roof replaced and no remaining actions required). Contractor payments are released from your escrow as each milestone is verified by CoreBids.' },
    ],
  },
  {
    id: 'payments-deposits',
    title: 'Payments & Deposits',
    description: 'Fund your project securely and unlock execution.',
    image: '/help/homeowner/08_project_start_tasks.png',
    imageAlt: 'Payment and deposit screen with highlighted action buttons',
    tags: ['Step-by-step', 'Notifications'],
    overview: 'Payment/deposit completion is a key gate that enables project execution and milestone flow.',
    steps: [
      { title: 'Open payment screen', body: 'Use your next-step banner or payment call-to-action.' },
      { title: 'Complete deposit/payment', body: 'Submit securely and verify confirmation state.' },
    ],
    nextStep: 'Milestone execution tracking becomes active.',
    notifications: [
      { title: 'Payment received', body: 'Confirms your payment state and progression.', type: 'success' },
      { title: 'Payment action required', body: 'Appears if payment is incomplete or interrupted.', type: 'action' },
    ],
    mistakesToAvoid: ['Leaving payment unfinished after starting checkout.'],
    faqs: [{ question: 'Where do I verify payment?', answer: 'Check your dashboard status and escrow/milestone area.' }],
  },
  {
    id: 'project-progress-milestones',
    title: 'Project Progress & Milestones',
    description: 'Track milestone progress and dispute if needed.',
    image: '/help/homeowner/10_milestones.png',
    imageAlt: 'Milestone timeline and progress view',
    tags: ['What happens next', 'Notifications'],
    overview: 'Milestones are reviewed and released by CoreBids — not by you directly. Your role is to monitor progress and dispute any milestone where the work does not match the agreement.',
    steps: [
      { title: 'Open milestone view', body: 'Use escrow/milestones from dashboard or project page.' },
      { title: 'Monitor milestone status', body: 'CoreBids reviews each milestone submission and releases payment once the work is verified.' },
      { title: 'Dispute if needed', body: 'If work does not match the agreement, you can file a dispute before payment is released. CoreBids reviews disputes and holds funds until resolved.' },
    ],
    nextStep: 'Verified milestones advance the project toward completion.',
    notifications: [
      { title: 'Milestone submitted', body: 'The contractor has uploaded evidence. CoreBids is reviewing.', type: 'info' },
      { title: 'Milestone released', body: 'Confirms CoreBids verified the milestone and released payment.', type: 'success' },
    ],
    mistakesToAvoid: ['Waiting to dispute — file a dispute before CoreBids releases payment if you see an issue.'],
    faqs: [{ question: 'What if work does not match?', answer: 'File a dispute through the milestone panel before payment is released. CoreBids reviews all disputes and holds funds until resolved. Contact support@corebids.com with details.' }],
  },
  {
    id: 'completion',
    title: 'Completion',
    description: 'Close out your project and keep final records.',
    image: '/help/homeowner/11_documents.png',
    imageAlt: 'Project completion and final documents panel',
    tags: ['Step-by-step', 'What happens next'],
    overview: 'Completion confirms your project lifecycle and ensures final documentation is accessible.',
    steps: [
      { title: 'Confirm final work status', body: 'Verify project outcomes before final close.' },
      { title: 'Review project documents', body: 'Open documents and save final records.' },
    ],
    nextStep: 'Your project is marked complete and retained in project history.',
    notifications: [{ title: 'Project completed', body: 'Final completion confirmation.', type: 'success' }],
    mistakesToAvoid: ['Not saving final documents for future reference.'],
    faqs: [{ question: 'Where are final files?', answer: 'In your Documents section and project history views.' }],
  },
  // CB:BEGIN homeowner-messaging-help
  {
    id: 'messaging-your-contractor',
    title: 'Messaging Your Contractor',
    description: 'Use in-platform chat to communicate with your contractor after a bid is accepted.',
    image: '/help/homeowner/07_dashboard_active.png',
    imageAlt: 'Project messaging panel with contractor',
    tags: ['What to click', 'Common issues'],
    overview: 'Once you accept a bid and your contractor is confirmed, a project chat panel appears. All messages stay on the platform — no phone numbers or personal emails needed.',
    steps: [
      { title: 'Open your project', body: 'Go to your active project from the dashboard.' },
      { title: 'Find the Messages tab or chat panel', body: 'A chat area appears on your project page after contractor selection.' },
      { title: 'Send a message', body: 'Type your message and submit. The contractor receives an email notification and can reply in the platform.' },
    ],
    nextStep: 'Use messaging to confirm start dates, ask scope questions, or flag concerns before they become disputes.',
    notifications: [
      { title: 'New message from contractor', body: 'You receive an email when your contractor sends a message. Click the link to reply.', type: 'info' },
    ],
    mistakesToAvoid: [
      'Sharing phone numbers or email addresses in chat — CoreBids monitors messages to protect both parties.',
      'Using chat to request scope changes — file a change order through the proper channel instead.',
    ],
    faqs: [
      { question: 'Can my contractor start messaging before the bid is accepted?', answer: 'No — messaging is only available after you select a contractor.' },
      { question: 'Are messages stored?', answer: 'Yes. All messages are logged in your project history for reference and dispute support.' },
      { question: 'What if my contractor is not responding?', answer: 'If there is no response within 48 hours, contact support with your project ID. Unresponsive contractors are tracked.' },
    ],
  },
  // CB:END homeowner-messaging-help
  {
    id: 'notifications-explained',
    title: 'Notifications Explained',
    description: 'Understand alerts and which ones need immediate action.',
    image: '/help/homeowner/13_help_notifications.png',
    imageAlt: 'Notification examples and meanings',
    tags: ['Notifications', 'Common issues'],
    overview: 'Notifications keep your project moving. Action-required alerts should be handled first.',
    steps: [
      { title: 'Prioritize Action Required', body: 'These are blocking tasks that pause progress if ignored.' },
      { title: 'Use direct links from notifications', body: 'Jump directly to the correct page/section.' },
    ],
    nextStep: 'Timely action reduces delays and support needs.',
    notifications: [
      { title: 'New bid', body: 'A new contractor proposal is ready.', type: 'info' },
      { title: 'Milestone ready', body: 'Contractor submitted for your review.', type: 'action' },
      { title: 'Completion', body: 'Project reached final state.', type: 'success' },
    ],
    mistakesToAvoid: ['Ignoring action-required messages.', 'Waiting days to review milestone notifications.'],
    faqs: [{ question: 'Where do notifications appear?', answer: 'In-app status surfaces and email notifications.' }],
  },
  {
    id: 'common-questions',
    title: 'Common Questions',
    description: 'Fast answers to frequent homeowner concerns.',
    image: '/help/homeowner/04_dashboard_bids_pane.png',
    imageAlt: 'General homeowner help and FAQ context',
    tags: ['FAQ', 'Support topics'],
    overview: 'Use this section when you are unsure which workflow stage your question belongs to.',
    steps: [
      { title: 'Search by keyword', body: 'Try terms like bid, payment, milestone, or completion.' },
      { title: 'Open linked sections', body: 'Jump directly to the most relevant workflow guidance.' },
    ],
    nextStep: 'If still blocked, contact support with your project ID.',
    notifications: [{ title: 'Support response', body: 'Follow-up from support team via current support channel.', type: 'info' }],
    mistakesToAvoid: ['Opening a ticket before checking relevant step guidance.'],
    faqs: [
      { question: 'Can I pause my project?', answer: 'Contact support to discuss scheduling/exception options.' },
      { question: 'Who can see my project?', answer: 'Only required platform participants and system roles for workflow execution.' },
    ],
  },
  // CB:BEGIN homeowner-portal-claim-help
  {
    id: 'claiming-realtor-quotes',
    title: 'Claiming Pre-Prepared Quotes from Your Realtor',
    description: 'Your realtor has already collected quotes for your property. Use the link they sent to review them and get started immediately — no intake forms needed.',
    image: '/help/homeowner/portal_claim.png',
    imageAlt: 'Portal landing page showing property address and number of quotes ready',
    tags: ['Step-by-step', 'What happens next'],
    overview: 'If your realtor sent you a CoreBids quote link, contractors have already submitted bids on your property. Clicking the link skips the normal setup process — you go straight to comparing bids and choosing a contractor.',
    steps: [
      { title: 'Open the link your realtor sent', body: 'Click the link in the email from CoreBids (subject: "Your Quotes Are Ready"). You will see a preview page showing the property address and how many quotes are waiting.' },
      { title: 'Create or sign into your CoreBids account', body: 'You need a homeowner account to claim the quotes. If you already have an account, sign in. If not, create one — it takes under a minute.' },
      { title: 'Confirm and claim the project', body: 'On the confirmation screen, review the terms and click "Claim My Quotes." The project is created in your dashboard immediately.' },
      { title: 'Compare the quotes', body: 'Your dashboard shows all contractor bids side by side — company name, price, timeline, and warranty. Take your time reviewing.' },
      { title: 'Select a contractor', body: 'When you are ready, click "Select Contractor" on the bid you want. This locks in your choice and starts the next steps.' },
    ],
    nextStep: 'After selecting a contractor, you will be guided through payment setup and project scheduling.',
    notifications: [
      { title: 'Quote link received', body: 'The email subject is "Your Quotes Are Ready — [property address]." Check spam if you do not see it.', type: 'info' },
      { title: 'Project created', body: 'Once you claim, you will see a confirmation and be taken directly to your bid comparison dashboard.', type: 'success' },
    ],
    mistakesToAvoid: [
      'Waiting too long to open the link — quote links expire after 7 days. If yours has expired, ask your realtor to resend it.',
      'Creating a contractor or realtor account instead of a homeowner account — if you see a "wrong role" error, you are signed into the wrong type of account. Sign out and create a homeowner account.',
      'Closing the browser right after claiming — the project is saved, but bookmark your dashboard link so you can return easily.',
    ],
    faqs: [
      { question: 'I already have a CoreBids account. Can I still use the link?', answer: 'Yes. Sign in with your existing account and claim the project. It will be added to your existing dashboard alongside any other projects you have.' },
      { question: 'The link says "expired." What do I do?', answer: 'Ask your realtor to resend the link. They can generate a fresh 7-day link from their dashboard in a few clicks.' },
      { question: 'The link says "revoked." What does that mean?', answer: 'Your realtor has cancelled this link, usually to send you an updated one with different quotes. Ask them to send the new link.' },
      { question: 'I claimed the link but cannot find my project.', answer: 'Your project is in your homeowner dashboard under "My Projects." If you signed in with a different email than you used to claim, it may be on a different account. Contact support with your email address and the property address.' },
      { question: 'Can I still negotiate with the contractor after selecting them?', answer: 'Selecting a contractor through CoreBids locks in the bid price as submitted. If you want to discuss scope or pricing changes, contact the contractor directly before selecting. Post-selection changes require a formal change order.' },
      { question: 'Does my realtor see which contractor I picked?', answer: 'Your realtor receives a notification when you claim the project. They are not automatically notified of which contractor you select, but your project is visible to them as an attributed referral.' },
      { question: 'Is the roof report my realtor attached visible to me?', answer: 'Yes. If your realtor attached a roof measurement report, it is available in your project documents after you claim.' },
    ],
  },
  // CB:END homeowner-portal-claim-help
  {
    id: 'contact-support',
    title: 'Contact Support',
    description: 'Escalate quickly with complete context.',
    image: '/help/homeowner/04_dashboard_bids_pane.png',
    imageAlt: 'Support CTA and contact guidance',
    tags: ['Support', 'Action required'],
    overview: 'When self-service is not enough, provide complete context so support can resolve quickly.',
    steps: [
      { title: 'Include project ID', body: 'Share the exact project identifier.' },
      { title: 'Describe where you are blocked', body: 'Mention the page/step and expected outcome.' },
      { title: 'Attach screenshot if possible', body: 'Visual context reduces back-and-forth.' },
    ],
    nextStep: 'Support follows up with the fastest path to unblock you.',
    notifications: [{ title: 'Support follow-up', body: 'Reply with requested details promptly.', type: 'action' }],
    mistakesToAvoid: ['Sending vague “it does not work” messages without context.'],
    faqs: [{ question: 'What is the best support format?', answer: 'Project ID + screenshot + step where issue occurs.' }],
  },
];
// CB:END homeowner-help-content

